import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

// Every tab is its own lazily loaded chunk (route-level code splitting).
// To add a tool: create the component and add ONE entry to TABS below.
// The `id` is the URL path segment (e.g. /albums).

const PhotosPage = lazy(() => import('./components/PhotosPage'));
const AlbumManager = lazy(() => import('./components/AlbumManager').then(m => ({ default: m.AlbumManager })));
const SearchGrid = lazy(() => import('./components/SearchGrid').then(m => ({ default: m.SearchGrid })));
const PeopleManager = lazy(() => import('./components/PeopleManager').then(m => ({ default: m.PeopleManager })));
const MapViewer = lazy(() => import('./components/MapViewer').then(m => ({ default: m.MapViewer })));
const Vault = lazy(() => import('./components/Vault').then(m => ({ default: m.Vault })));
const BillingSettings = lazy(() => import('./components/BillingSettings').then(m => ({ default: m.BillingSettings })));
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const FavoritesPage = lazy(() => import('./components/FavoritesPage'));
const TrashPage = lazy(() => import('./components/TrashPage'));
const ArchivePage = lazy(() => import('./components/ArchivePage'));
const CleanUpPage = lazy(() => import('./components/CleanUpPage'));

/** Shared state mapped into each tab's props via `TabDef.props`. */
export interface TabContext {
  tenantId: string;
  gridLayout: 'square' | 'justified';
  searchQuery: string;
  onboardingIntent?: string | null;
  isCorporateDomain?: boolean;
}

export interface TabDef {
  id: string;
  label: string;
  /** Shorter label for the mobile bottom nav */
  mobileLabel?: string;
  icon: string;
  /** Sidebar group. Omit to keep the tab out of the sidebar. */
  section?: 'main' | 'library';
  /** Show in mobile bottom nav */
  mobile?: boolean;
  adminOnly?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: LazyExoticComponent<ComponentType<any>>;
  /** Maps shared context to component props */
  props?: (ctx: TabContext) => Record<string, unknown>;
}

const tenant = ({ tenantId }: TabContext) => ({ tenantId });

export const TABS: TabDef[] = [
  { id: 'photos', label: 'Photos', icon: '🖼️', section: 'main', mobile: true, component: PhotosPage,
    props: ({ tenantId, gridLayout }) => ({ tenantId, gridLayout }) },
  { id: 'explore', label: 'Explore', mobileLabel: 'Search', icon: '🔍', section: 'main', mobile: true, component: SearchGrid,
    props: ({ tenantId, searchQuery }) => ({ tenantId, query: searchQuery }) },
  { id: 'map', label: 'Map', icon: '🗺️', section: 'main', component: MapViewer, props: tenant },
  { id: 'sharing', label: 'Sharing', icon: '👥', section: 'main', mobile: true, component: PeopleManager, props: tenant },

  { id: 'favorites', label: 'Favorites', icon: '🤍', section: 'library', component: FavoritesPage,
    props: ({ tenantId, gridLayout }) => ({ tenantId, gridLayout }) },
  { id: 'albums', label: 'Albums', mobileLabel: 'Library', icon: '📁', section: 'library', mobile: true, component: AlbumManager, props: tenant },
  { id: 'vault', label: 'Vault', icon: '🔒', section: 'library', component: Vault, props: tenant },
  { id: 'cleanup', label: 'Clean Up', icon: '🧹', section: 'library', component: CleanUpPage, props: tenant },
  { id: 'archive', label: 'Archive', icon: '📦', section: 'library', component: ArchivePage, props: tenant },
  { id: 'trash', label: 'Trash', icon: '🗑️', section: 'library', component: TrashPage, props: tenant },

  { id: 'settings', label: 'Settings', icon: '⚙️', component: BillingSettings,
    props: ({ onboardingIntent, isCorporateDomain }) => ({ onboardingIntent, isCorporateDomain }) },
  { id: 'admin', label: 'Admin', icon: '🛡️', adminOnly: true, component: AdminDashboard },
];

export const DEFAULT_TAB = 'photos';

export const getTab = (id: string): TabDef | undefined => TABS.find(t => t.id === id);

/** Legacy `/#albums` links -> tab id (or default). */
export const tabFromLegacyHash = (hash: string, isAdmin: boolean): string => {
  const clean = hash.replace(/^#\/?/, '').split('?')[0];
  const tab = getTab(clean);
  return !tab || (tab.adminOnly && !isAdmin) ? DEFAULT_TAB : tab.id;
};
